import { useEffect, useState } from "react";
import * as asn1js from "asn1js";
import * as pkijs from "pkijs";

import { pemToDer, toArrayBuffer } from "../lib/bytes";
import { dnToString } from "../lib/parse";
import { certSha256Fingerprint } from "../lib/verify";

const CA_SLUGS = ["root", "doc1", "enc1", "id1", "dev1"] as const;

interface CaRow {
  slug: string;
  subject: string;
  notAfter: string;
  fingerprint: string;
}

interface ProfileMeta {
  ca: string;
  key: string;
  max_validity_days: number;
  san_policy: string;
  description: string;
}

/** Same-origin only: lists the CA hierarchy, profiles, CRLs, and SSH CA keys. */
export function CaPage() {
  const [rows, setRows] = useState<CaRow[] | null>(null);
  const [sshCas, setSshCas] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileMeta> | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const found: CaRow[] = [];
      for (const slug of CA_SLUGS) {
        const path = slug === "root" ? "/ca/roots.pem" : `/ca/certs/${slug}.pem`;
        try {
          const resp = await fetch(path);
          if (!resp.ok) continue;
          const [der] = pemToDer(await resp.text());
          if (!der) continue;
          const asn1 = asn1js.fromBER(toArrayBuffer(der));
          const cert = new pkijs.Certificate({ schema: asn1.result });
          found.push({
            slug,
            subject: dnToString(cert.subject),
            notAfter: cert.notAfter.value.toISOString().slice(0, 10),
            fingerprint: await certSha256Fingerprint(cert),
          });
        } catch {
          // absent CA — skip
        }
      }
      const ssh: string[] = [];
      for (const name of ["user-ca", "host-ca"]) {
        try {
          const resp = await fetch(`/ca/ssh/${name}.pub`);
          if (resp.ok) ssh.push(`${name}: ${(await resp.text()).trim()}`);
        } catch {
          // not published
        }
      }
      let fetchedProfiles: Record<string, ProfileMeta> | null = null;
      try {
        const resp = await fetch("/ca/profiles");
        if (resp.ok) fetchedProfiles = await resp.json();
      } catch {
        // profiles endpoint down — leave the section empty
      }
      if (!cancelled) {
        setRows(found);
        setSshCas(ssh);
        setProfiles(fetchedProfiles);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="ca-page">
      <section className="panel">
        <h3>Certificate authority hierarchy</h3>
        <p className="muted">
          A private PKI: install the root explicitly to rely on it. Nothing on
          this page calls anywhere except this site.
        </p>
        {rows === null ? (
          <p className="muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="muted">No CA certificates are published here.</p>
        ) : (
          <div className="cert-cards">
            {rows.map((row) => (
              <div key={row.slug} className="cert-card">
                <div className="cert-subject">{row.subject}</div>
                <dl className="kv">
                  <dt>Valid until</dt>
                  <dd>{row.notAfter}</dd>
                  <dt>SHA-256</dt>
                  <dd>
                    <code className="mono">{row.fingerprint}</code>
                  </dd>
                  <dt>Download</dt>
                  <dd>
                    {row.slug === "root" ? (
                      <a href="/ca/roots.pem">PEM</a>
                    ) : (
                      <>
                        <a href={`/ca/certs/${row.slug}.pem`}>PEM</a> ·{" "}
                        <a href={`/ca/certs/${row.slug}.der`}>DER</a> ·{" "}
                        <a href={`/crl/${row.slug}.crl`}>CRL</a>
                      </>
                    )}
                  </dd>
                </dl>
              </div>
            ))}
          </div>
        )}
      </section>
      {profiles && Object.keys(profiles).length > 0 && (
        <section className="panel">
          <h3>Issuance profiles</h3>
          <p className="muted">
            Leaves the CA will issue. Each profile is pinned at issuance; the
            CSR supplies only the CN and, where the profile allows, a SAN.
          </p>
          <table className="ca-profiles">
            <thead>
              <tr>
                <th>Profile</th>
                <th>CA</th>
                <th>Max validity</th>
                <th>SAN</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(profiles).map(([name, p]) => (
                <tr key={name}>
                  <td>
                    <code className="mono">{name}</code>
                  </td>
                  <td className="mono">{p.ca}</td>
                  <td>{p.max_validity_days} days</td>
                  <td className="mono">{p.san_policy}</td>
                  <td>{p.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {sshCas.length > 0 && (
        <section className="panel">
          <h3>SSH certificate authorities</h3>
          {sshCas.map((line) => (
            <p key={line} className="mono" style={{ overflowWrap: "anywhere" }}>
              {line}
            </p>
          ))}
          <p className="muted">
            Hosts trust user certs via{" "}
            <code className="mono">TrustedUserCAKeys</code>; clients pin hosts
            with an <code className="mono">@cert-authority</code> line.
          </p>
        </section>
      )}
      <section className="panel">
        <h3>Policy</h3>
        <p>
          <a href="/cps.html">Certification Practice Statement</a> ·{" "}
          <a href="/ca/policy">Certificate policy summary</a> ·{" "}
          <a href="/policy">Timestamping policy</a>
        </p>
      </section>
    </div>
  );
}
